import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from './AppIcon';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks';
import { getCandidatesCount } from '../lib/api';
import {
  getCandidatesOverLimitCount,
  isSubscriptionOverLimit,
} from '../lib/utils/planLimits';
import { CANDIDATES_LIST_PATH } from '../lib/utils/candidateListRoutes';

const BANNER_HEIGHT_VAR = '--over-limit-banner-height';

/** Survive MainLayout remounts between page navigations. */
const excessCache = new Map();

const OverLimitBanner = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { profile } = useProfile();
  const show = isSubscriptionOverLimit(profile);
  const userId = user?.id ?? null;

  const [excess, setExcess] = useState(() => (
    userId != null ? (excessCache.get(userId) ?? null) : null
  ));
  const bannerRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (!userId) {
      setExcess(null);
      return undefined;
    }

    if (!show) {
      // Only clear when we know the user is no longer over limit.
      if (profile != null) {
        excessCache.delete(userId);
        setExcess(null);
      }
      return undefined;
    }

    const cached = excessCache.get(userId);
    if (cached != null) {
      setExcess(cached);
    }

    const requestId = ++requestIdRef.current;
    let cancelled = false;

    getCandidatesCount(userId)
      .then((count) => {
        if (cancelled || requestId !== requestIdRef.current) return;
        const next = getCandidatesOverLimitCount(count, profile?.max_candidates);
        excessCache.set(userId, next);
        setExcess(next);
      })
      .catch((err) => {
        console.error('Failed to load candidates count for over-limit banner:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [show, userId, profile, profile?.max_candidates, profile?.candidates_remaining]);

  useEffect(() => {
    const root = document.documentElement;

    if (!show) {
      root.style.removeProperty(BANNER_HEIGHT_VAR);
      return undefined;
    }

    const syncHeight = () => {
      const height = bannerRef.current?.offsetHeight ?? 0;
      root.style.setProperty(BANNER_HEIGHT_VAR, `${height}px`);
    };

    syncHeight();
    window.addEventListener('resize', syncHeight);

    let observer;
    if (typeof ResizeObserver !== 'undefined' && bannerRef.current) {
      observer = new ResizeObserver(syncHeight);
      observer.observe(bannerRef.current);
    }

    return () => {
      window.removeEventListener('resize', syncHeight);
      observer?.disconnect();
      root.style.removeProperty(BANNER_HEIGHT_VAR);
    };
  }, [show, excess]);

  if (!show) return null;

  const deleteCount = excess != null && excess > 0 ? excess : null;

  return (
    <div
      ref={bannerRef}
      role="alert"
      className="fixed inset-x-0 top-0 z-[110] border-b border-[#f59e4a]/50 bg-[#fca658] text-orange-950 shadow-sm"
    >
      <div className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-6 lg:px-8">
        <div className="flex min-w-0 items-start gap-3 sm:items-center">
          <span className="mt-0.5 inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-orange-900 sm:mt-0">
            <Icon name="AlertTriangle" size={20} className="text-white" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold sm:text-base">
              {deleteCount != null
                ? t('subscription.overLimitTitle', { count: deleteCount })
                : t('subscription.overLimitTitleUnknown')}
            </p>
            <p className="mt-0.5 text-sm text-orange-950/80">
              {t('subscription.overLimitBody')}
            </p>
          </div>
        </div>
        <Link
          to={CANDIDATES_LIST_PATH}
          className="inline-flex h-9 flex-shrink-0 items-center justify-center rounded-md bg-orange-900 px-4 text-sm font-semibold text-orange-50 transition-colors hover:bg-orange-950"
        >
          {t('subscription.overLimitCta')}
        </Link>
      </div>
    </div>
  );
};

export default OverLimitBanner;
